"use client";

import { useEffect, useState } from "react";
import {
  getSavedProfiles,
  getActiveProfileId,
  setActiveProfileId,
  type SavedChartProfile,
} from "@/lib/storage/chart-vault";

interface ChartVaultSelectorProps {
  /**
   * Optional: server pages mount the selector bare (an RSC page cannot pass a
   * function across the client boundary — the /free-kundali prerender crash);
   * selection still persists to the vault, and client consumers may wire this
   * to react in-page.
   */
  onSelectProfile?: (profile: SavedChartProfile) => void;
  isHindi?: boolean;
}

export default function ChartVaultSelector({
  onSelectProfile,
  isHindi = false,
}: ChartVaultSelectorProps) {
  const [profiles, setProfiles] = useState<SavedChartProfile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    // Sync profiles on mount from browser storage
    const sync = () => {
      setProfiles(getSavedProfiles());
      setActiveId(getActiveProfileId());
    };
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  if (profiles.length === 0) return null;

  const handleSelect = (profile: SavedChartProfile) => {
    setActiveProfileId(profile.id);
    setActiveId(profile.id);
    onSelectProfile?.(profile);
  };

  const RELATION_LABELS: Record<string, { en: string; hi: string }> = {
    self: { en: "Self", hi: "स्वयं" },
    spouse: { en: "Spouse", hi: "जीवनसाथी" },
    child: { en: "Child", hi: "संतान" },
    parent: { en: "Parent", hi: "माता/पिता" },
    friend: { en: "Friend", hi: "मित्र" },
    other: { en: "Other", hi: "अन्य" },
  };

  return (
    <div className="mb-5 rounded-xl border border-border-default bg-surface-raised p-3.5 shadow-xs">
      <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-border-default">
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand flex items-center gap-1.5">
          <span>📁</span>
          <span>{isHindi ? "सहेजे गए प्रोफाइल (Saved Profiles)" : "Saved Family Profiles"}</span>
        </span>
        <span className="text-caption text-text-muted">
          {profiles.length} {isHindi ? "प्रोफाइल" : profiles.length === 1 ? "profile" : "profiles"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        {profiles.map((p) => {
          const isSelected = p.id === activeId;
          const relation = RELATION_LABELS[p.relation] || RELATION_LABELS.other;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSelect(p)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-caption font-semibold transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle text-intent-brand shadow-xs ring-1 ring-intent-brand/30"
                  : "border-border-default bg-surface-raised text-text-primary hover:bg-surface-warm"
              }`}
            >
              <span>{p.gender === "female" ? "👩" : "👨"}</span>
              <span>{p.name}</span>
              <span className="text-text-muted font-normal">
                ({isHindi ? relation.hi : relation.en})
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
